import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import aiChatService from '../../../../services/aiChatService';
import {
  ReportSpec,
  ReportTimeGranularity,
  ReportVisual,
  createReportId,
} from '../reportCatalog';
import DynamicReportCard from './DynamicReportCard';

interface BranchLike {
  id?: string;
  branchId?: string;
  name?: string;
}

interface AiReportDialogProps {
  open: boolean;
  onClose: () => void;
  /** "Ekle" — spec sihirbazla yapılmış gibi kaydedilir (aynı localStorage deposu) */
  onSave: (spec: ReportSpec) => void;
  /** "Düzenle" — spec sihirbazda açılır, kullanıcı adım adım düzeltebilir */
  onEditInBuilder: (spec: ReportSpec) => void;
  branches: BranchLike[];
  /** Önizleme için dashboard'ın aktif tarih aralığı */
  startDate: string;
  endDate: string;
}

/**
 * AI rapor asistanı: kullanıcı serbest metin yazar, backend LLM ile ReportSpec üretir
 * (whitelist doğrulamasından geçmiş olarak döner), önizleme kartı gösterilir.
 * Kullanıcı Ekle / Sihirbazda Düzenle / Vazgeç seçer — AI hiçbir şeyi kendiliğinden kaydetmez.
 */
const AiReportDialog: React.FC<AiReportDialogProps> = ({
  open,
  onClose,
  onSave,
  onEditInBuilder,
  branches,
  startDate,
  endDate,
}) => {
  const { t } = useTranslation('accounting');

  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestedSpec, setSuggestedSpec] = useState<ReportSpec | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const resetResult = () => {
    setSuggestedSpec(null);
    setNote(null);
    setError(null);
  };

  const handleClose = () => {
    setText('');
    setLoading(false);
    resetResult();
    onClose();
  };

  const handleGenerate = async () => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    setLoading(true);
    resetResult();
    try {
      const branchList = branches
        .filter((b) => (b.id || b.branchId) && b.name)
        .map((b) => ({ id: (b.id || b.branchId)!, name: b.name! }));
      const result = await aiChatService.suggestDynamicReport(trimmed, branchList);
      setSuggestedSpec({
        id: createReportId(),
        title: result.spec.title,
        dataSource: result.spec.dataSource,
        metric: result.spec.metric,
        dimension: result.spec.dimension,
        timeGranularity:
          result.spec.dimension === 'time'
            ? ((result.spec.timeGranularity ?? 'day') as ReportTimeGranularity)
            : undefined,
        filters: result.spec.filters,
        visual: result.spec.visual as ReportVisual,
        limit: result.spec.limit,
      });
      setNote(result.note);
    } catch (err: any) {
      console.error('[AiReportDialog] öneri alınamadı:', err);
      setError(err?.message || t('dashboard.reportBuilder.ai.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (!suggestedSpec) return;
    onSave(suggestedSpec);
    handleClose();
  };

  const handleEdit = () => {
    if (!suggestedSpec) return;
    onEditInBuilder(suggestedSpec);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" />
          <Typography variant="h6" component="span" fontWeight="bold">
            {t('dashboard.reportBuilder.ai.title')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
          {t('dashboard.reportBuilder.ai.hint')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            size="small"
            placeholder={t('dashboard.reportBuilder.ai.placeholder')}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleGenerate();
              }
            }}
            inputProps={{ maxLength: 500 }}
            disabled={loading}
          />
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={loading || !text.trim()}
            sx={{ flexShrink: 0, minWidth: 44, px: 1.5, height: 56 }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon fontSize="small" />}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {suggestedSpec && (
          <Box sx={{ mt: 2 }}>
            {note && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                {note}
              </Alert>
            )}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('dashboard.reportBuilder.ai.previewLabel')}
            </Typography>
            <Box sx={{ height: suggestedSpec.visual === 'kpi' ? 140 : 300 }}>
              <DynamicReportCard
                spec={suggestedSpec}
                startDate={startDate}
                endDate={endDate}
                branches={branches}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('dashboard.reportBuilder.ai.cancel')}</Button>
        <Box sx={{ flexGrow: 1 }} />
        {suggestedSpec && (
          <>
            <Button onClick={handleEdit}>{t('dashboard.reportBuilder.ai.editInWizard')}</Button>
            <Button variant="contained" onClick={handleAdd}>
              {t('dashboard.reportBuilder.ai.add')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AiReportDialog;
